import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useApp } from '../context/AppContext';
import { useLoading } from '../context/LoadingContext';
import { useToast } from '../context/ToastContext';
import { useDisplayTimezone } from '../context/TimeModeContext';
import { toRelativeOrTime, getCstDateKey, todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import StatusPill from '../components/StatusPill';
import TodayBadge from '../components/TodayBadge';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

export default function CheckIn() {
  const { currentUser, getLatestLogFor, addLog, setIdToken, isIdTokenValid } = useApp();
  const { withLoading } = useLoading();
  const { showSuccess, showError } = useToast();
  const displayTz = useDisplayTimezone(currentUser);
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const latest = getLatestLogFor(currentUser.uuid);
  const isCheckedIn = latest ? latest.isIn : false;
  const status = !latest ? 'none' : isCheckedIn ? 'in' : 'out';

  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  // Someone whose last action was checking out earlier *today* (CST) is
  // about to start a second session — not wrong (breaks, corrections happen)
  // but worth a nudge rather than silently letting it slide.
  const alreadyCheckedInToday =
    !isCheckedIn && latest && getCstDateKey(latest.logtime_utc) === todayDateStringInTz(DAY_ANCHOR_TZ);

  const handleConfirm = async () => {
    const nextIsIn = !isCheckedIn;
    try {
      await withLoading(() => addLog({ isIn: nextIsIn, notes: note }));
      setConfirmOpen(false);
      setNote('');
      showSuccess(nextIsIn ? "You're checked in." : "You're checked out.");
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-6 sm:py-10">
        <div className="flex justify-center mb-4">
          <TodayBadge timezone={DAY_ANCHOR_TZ} />
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-5 sm:p-6">
          <p className="text-xs sm:text-sm text-muted">Hey {currentUser.name.split(' ')[0]},</p>
          <h1 className="text-lg sm:text-xl font-semibold text-ink mt-1">Your status</h1>

          <div className="mt-4">
            <StatusPill status={status} />
          </div>

          {latest && (
            <p className="mt-2 text-xs sm:text-sm text-muted">
              Last action {toRelativeOrTime(latest.logtime_utc, displayTz)}
              {latest.notes ? ` — "${latest.notes}"` : ''}
            </p>
          )}

          <div className="mt-6">
            <label className="block text-sm font-medium text-ink mb-1">
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={isCheckedIn ? "What'd you get done today?" : "What are you starting with?"}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
            />
          </div>

          <button
            onClick={() => setConfirmOpen(true)}
            className={`mt-6 w-full py-3 rounded-xl font-semibold text-white transition-colors ${
              isCheckedIn ? 'bg-bronze hover:bg-ink' : 'bg-gold hover:bg-bronze'
            }`}
          >
            {isCheckedIn ? 'Check Out' : 'Check In'}
          </button>
        </div>

        <Link
          to="/weekly"
          className="block text-center mt-6 text-sm font-medium text-bronze hover:text-ink transition-colors"
        >
          View this week's summary →
        </Link>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={isCheckedIn ? 'Confirm check out' : 'Confirm check in'}
        confirmLabel={isCheckedIn ? 'Check Out' : 'Check In'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
        footer={
          needsReauth ? (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-muted text-center">
                Your session needs a quick refresh before this can be saved.
              </p>
              <GoogleLogin
                onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
                onError={() => showError('Google sign-in failed — please try again.')}
              />
              <button
                onClick={() => setConfirmOpen(false)}
                className="text-sm font-medium text-muted hover:text-ink transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : undefined
        }
      >
        {alreadyCheckedInToday && (
          <p className="mb-3 rounded-lg bg-info-bg text-info-tx px-3 py-2 text-sm font-medium">
            You've already checked in today — are you sure you want to check in again?
          </p>
        )}
        <p>
          You're about to {isCheckedIn ? 'check out' : 'check in'} as{' '}
          <span className="font-medium">{currentUser.name}</span>.
        </p>
        <div className="mt-3 rounded-lg bg-card-tint border border-border px-3 py-2">
          <p className="text-xs font-medium text-muted uppercase tracking-wide mb-1">Your note</p>
          <p className={note ? 'text-ink' : 'text-muted italic'}>
            {note || 'No note added.'}
          </p>
        </div>
      </ConfirmModal>
    </Layout>
  );
}
